/**
 * Validação das variáveis de ambiente obrigatórias, executada no `main.tsx` antes de
 * montar o React.
 *
 * Sem isto, um deploy que esquece a variável não falha: `VITE_API_URL` vira `undefined`
 * e a aplicação passa a pedir `undefined/api/escalas`. O usuário vê uma tela vazia com
 * um erro genérico, e nada aponta para a causa. Falhar aqui é melhor — a mensagem diz
 * qual variável falta e a aplicação nem chega a subir mal configurada.
 */

/**
 * As que não têm default possível: a aplicação sempre trabalha com a API (a demonstração
 * é carregada na própria igreja, em Configurações › Dados da igreja).
 */
export const REQUIRED: readonly string[] = ['VITE_API_URL'];

export class MissingEnvError extends Error {
  readonly missing: string[];

  constructor(missing: string[]) {
    super(
      `Configuração ausente: ${missing.join(', ')}. ` +
        'Defina no .env (desenvolvimento) ou nas variáveis do build (deploy).'
    );
    this.name = 'MissingEnvError';
    this.missing = missing;
  }
}

/**
 * `env` é injetável para o teste: em produção vem do `import.meta.env`, que o Vite
 * substitui em tempo de build (não dá para reescrever em runtime).
 *
 * `VITE_BASE_PATH` fica de fora de propósito: vazio é um valor legítimo (aplicação
 * servida na raiz).
 */
export function assertRequiredEnv(
  env: Record<string, string | undefined> = import.meta.env,
  required: readonly string[] = REQUIRED
): void {
  const missing = required.filter((name) => {
    const value = env[name];

    return typeof value !== 'string' || value.trim() === '';
  });

  if (missing.length > 0) {
    throw new MissingEnvError(missing);
  }
}
